// CONTRACT:C2-RENDER-ENGINE.1.0
/** Layer manager — add/update/remove LayerSpec -> LayerHandle, with the
 *  deck.gl `updateTriggers` discipline (## Behavioral Contracts).
 *
 *  Pure logic, no GPU: it OWNS the spec/handle registry and computes the
 *  minimal-invalidation diff between an old and a new spec. The rule the
 *  contract pins is that an unchanged-data tick must NOT rebuild a layer —
 *  only the changed attributes' updateTriggers flip. The engine (engine.ts)
 *  consumes `LayerDiff` to drive the backend adapter.
 *
 *  This file is FULLY UNIT-TESTED.
 */

import type { LayerHandle, LayerSpec } from "../../contracts-ts/src/index.ts";
import { ErrLayerUnknown } from "./errors.ts";

/** The minimal set of changes a tick implies for one layer. */
export interface LayerDiff {
  /** Props whose value changed (shallow-compared). */
  changedProps: string[];
  /** updateTriggers keys whose value changed — ONLY these invalidate GPU attrs. */
  changedTriggers: string[];
  /** Whether structural fields (type/kinds/lod) changed — forces a rebuild. */
  rebuild: boolean;
  /** True when nothing changed: the engine MUST skip work entirely. */
  noop: boolean;
}

/** Internal registry record: the live spec behind an issued handle. */
interface LayerRecord {
  handle: LayerHandle;
  spec: LayerSpec;
}

/** Shallow value equality good enough for deck prop/trigger comparison.
 *  Primitives compared by ===; arrays/objects compared one level deep so a
 *  new array literal with identical entries does not count as a change. */
function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) {
    return false;
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => Object.is(v, b[i]));
  }
  const ka = Object.keys(a as Record<string, unknown>);
  const kb = Object.keys(b as Record<string, unknown>);
  if (ka.length !== kb.length) return false;
  return ka.every((k) =>
    Object.is((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
  );
}

function diffKeys(
  prev: Record<string, unknown> | undefined,
  next: Record<string, unknown> | undefined,
): string[] {
  const changed: string[] = [];
  const keys = new Set([...Object.keys(prev ?? {}), ...Object.keys(next ?? {})]);
  for (const k of keys) {
    if (!shallowEqual(prev?.[k], next?.[k])) changed.push(k);
  }
  return changed;
}

/** Compute the diff between an existing spec and the spec it would become.
 *  Exported so tests can exercise the discipline directly. */
export function computeDiff(prev: LayerSpec, next: LayerSpec): LayerDiff {
  const rebuild =
    prev.type !== next.type ||
    !shallowEqual(prev.kinds, next.kinds) ||
    !shallowEqual(prev.lod, next.lod);
  const changedProps = diffKeys(prev.props, next.props);
  const changedTriggers = diffKeys(prev.updateTriggers, next.updateTriggers);
  const noop =
    !rebuild && changedProps.length === 0 && changedTriggers.length === 0;
  return { changedProps, changedTriggers, rebuild, noop };
}

let handleSeq = 0;

/** Registry of layers and the source of truth for updateTriggers diffs. */
export class LayerManager {
  private readonly records = new Map<string, LayerRecord>();
  /** Kinds this build can draw via a deck layer `type`. Unsupported ⇒ ErrLayerUnknown. */
  private readonly supportedTypes: ReadonlySet<string>;

  constructor(
    supportedTypes: Iterable<string> = [
      "ScatterplotLayer",
      "IconLayer",
      "PathLayer",
      "PolygonLayer",
      "GeoJsonLayer",
      "H3HexagonLayer",
      "TextLayer",
      "ArcLayer",
    ],
  ) {
    this.supportedTypes = new Set(supportedTypes);
  }

  get count(): number {
    return this.records.size;
  }

  /** Register a layer. Throws ErrLayerUnknown if the deck `type` is unsupported
   *  by this build, or if the spec id is already registered. */
  add(spec: LayerSpec): LayerHandle {
    if (!this.supportedTypes.has(spec.type)) {
      throw new ErrLayerUnknown(`unsupported layer type "${spec.type}"`);
    }
    if (this.records.has(spec.id)) {
      throw new ErrLayerUnknown(`layer id "${spec.id}" already registered`);
    }
    const handle: LayerHandle = { id: `layer:${spec.id}#${++handleSeq}` };
    this.records.set(handle.id, { handle, spec: cloneSpec(spec) });
    return handle;
  }

  /** Patch a layer in place. Returns the diff so the engine flips only changed
   *  attributes. Throws ErrLayerUnknown for an unissued handle. A patch that
   *  changes nothing yields `noop:true` and the engine MUST skip a rebuild. */
  update(handle: LayerHandle, patch: Partial<LayerSpec>): LayerDiff {
    const rec = this.records.get(handle.id);
    if (!rec) throw new ErrLayerUnknown(`no layer for handle "${handle.id}"`);
    const next: LayerSpec = {
      ...rec.spec,
      ...patch,
      props: { ...rec.spec.props, ...(patch.props ?? {}) },
      updateTriggers: {
        ...(rec.spec.updateTriggers ?? {}),
        ...(patch.updateTriggers ?? {}),
      },
    };
    const diff = computeDiff(rec.spec, next);
    if (!diff.noop) rec.spec = cloneSpec(next);
    return diff;
  }

  /** Remove a layer. Throws ErrLayerUnknown for an unissued handle. */
  remove(handle: LayerHandle): void {
    if (!this.records.delete(handle.id)) {
      throw new ErrLayerUnknown(`no layer for handle "${handle.id}"`);
    }
  }

  /** Resolve a handle to its current spec, or undefined if not registered. */
  specFor(handle: LayerHandle): LayerSpec | undefined {
    return this.records.get(handle.id)?.spec;
  }

  /** True if this handle was issued by this manager and is still live. */
  has(handle: LayerHandle): boolean {
    return this.records.has(handle.id);
  }

  /** All live specs, in registration order. */
  list(): LayerSpec[] {
    return [...this.records.values()].map((r) => r.spec);
  }
}

function cloneSpec(spec: LayerSpec): LayerSpec {
  return {
    ...spec,
    kinds: [...spec.kinds],
    props: { ...spec.props },
    updateTriggers: spec.updateTriggers ? { ...spec.updateTriggers } : undefined,
    lod: spec.lod ? { ...spec.lod } : undefined,
  };
}
